/* Fluffy theme behaviour: cart drawer (Shopify Ajax Cart + Section Rendering API), favorites, mobile menu.
   All visible text comes from Liquid (locales) — this file only moves data around. */
(() => {
  const config = window.FluffyConfig || { routes: { root: '/' }, strings: {} };
  const root = config.routes.root || '/';
  const track = (name, data) => window.FluffyAnalytics?.track(name, data);

  /* ---------- Scroll lock that also works in iOS Safari ---------- */
  let lockCount = 0;
  let lockedScrollY = 0;
  function lockScroll() {
    if (lockCount++ > 0) return;
    lockedScrollY = window.scrollY;
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.classList.add('is-scroll-locked');
  }
  function unlockScroll() {
    if (lockCount === 0 || --lockCount > 0) return;
    document.body.classList.remove('is-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);
  }

  /* ---------- Cart ---------- */
  const drawer = () => document.getElementById('cart-drawer');

  let drawerOpeningFrame = null;

  function openDrawer() {
    const el = drawer();
    if (!el || drawerOpeningFrame !== null || el.classList.contains('is-open')) return;
    el.hidden = false;
    track('cart_opened', { source: 'drawer' });
    lockScroll();
    drawerOpeningFrame = requestAnimationFrame(() => {
      drawerOpeningFrame = null;
      el.classList.add('is-open');
      el.querySelector('.cart-drawer__panel').focus({ preventScroll: true });
    });
  }

  function closeDrawer() {
    const el = drawer();
    if (!el || (drawerOpeningFrame === null && !el.classList.contains('is-open'))) return;
    if (drawerOpeningFrame !== null) {
      cancelAnimationFrame(drawerOpeningFrame);
      drawerOpeningFrame = null;
    }
    el.classList.remove('is-open');
    unlockScroll();
    setTimeout(() => { if (!el.classList.contains('is-open')) el.hidden = true; }, 300);
  }

  function updateCartCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.toggleAttribute('data-empty', count === 0);
    });
  }

  function replaceFrom(sectionHtml, selector, current) {
    if (!sectionHtml || !current) return;
    const fresh = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector(selector);
    if (fresh) current.replaceWith(fresh);
  }

  /* Re-renders the cart drawer and, on /cart, the cart page section. */
  async function refreshCart() {
    const pageSections = [...document.querySelectorAll('[data-cart-section]')];
    const ids = ['cart-drawer', ...pageSections.map((el) => el.dataset.cartSection)];
    const [cartResponse, sectionsResponse] = await Promise.all([
      fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }),
      fetch(`${root}?sections=${ids.join(',')}`),
    ]);
    const cart = await cartResponse.json();
    if (window.FluffyGoogle) {
      window.FluffyGoogle.cart = {
        value: cart.total_price / 100,
        items: cart.items.map(item => ({ item_id: String(item.product_id), item_name: item.product_title, item_variant: String(item.variant_id), price: item.final_price / 100, quantity: item.quantity })),
      };
    }
    const sections = await sectionsResponse.json();
    updateCartCounts(cart.item_count);
    replaceFrom(sections['cart-drawer'], '[data-drawer-content]', drawer() && drawer().querySelector('[data-drawer-content]'));
    pageSections.forEach((el) => {
      replaceFrom(sections[el.dataset.cartSection], '[data-cart-section-content]', el.querySelector('[data-cart-section-content]'));
    });
    renderFavorites();
    document.dispatchEvent(new Event('fluffy:cart-rendered'));
    return cart;
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-add-to-cart-form]');
    if (!form) return;
    event.preventDefault();
    // Ajax owns this submission. Stop the native form observer from also
    // reporting AddToCart; Shopify tracks the successful cart/add.js response.
    event.stopPropagation();
    // The product page button sits outside its form (linked with the form attribute).
    // getAttribute: form.id would return the <input name="id"> field instead of the form's id.
    const buttons = [...form.elements].filter(el => el.matches('[data-add-to-cart]'));
    const states = buttons.map(button => ({ button, label: button.querySelector('[data-add-to-cart-label]'), disabled: button.disabled }));
    const setLabels = text => states.forEach(({ label }) => { label.textContent = text; });
    if (form.dataset.submitting === 'true') return;
    const section = form.closest('[data-product-section]');
    const picker = section && section.querySelector('[data-bundle-picker]');
    const extras = picker ? [...picker.querySelectorAll('[data-bundle-choice]:checked')] : [];
    const requiredExtras = picker ? Number(picker.dataset.packSize || 1) - 1 : 0;
    if (extras.length !== requiredExtras) {
      const error = picker.querySelector('[data-bundle-error]');
      error.textContent = `Please choose ${requiredExtras} extra ${requiredExtras === 1 ? 'case' : 'cases'} for your pack.`;
      error.hidden = false;
      return;
    }
    const body = new FormData(form);
    const pack = picker && picker.querySelector('[data-bundle-pack][aria-pressed="true"]');
    if (extras.length && ['Fluffy Case', 'Magnetic Case'].includes(picker.dataset.productType)) {
      if (!pack?.dataset.packVariant || pack.disabled) return;
      // Each selection has a fixed native variant. Never mutate shared bundle
      // components per shopper or silently fall back to a text-only manual pack.
      let bundleMap = {};
      try { bundleMap = JSON.parse(picker.querySelector('[data-native-bundle-map]')?.textContent || '{}'); } catch (_) { /* Fail closed below. */ }
      const key = [String(body.get('id')), ...extras.map((choice) => choice.value)].sort().join(',');
      const nativeBundle = bundleMap[key];
      if (!nativeBundle?.id) {
        const error = picker.querySelector('[data-bundle-error]');
        error.textContent = 'This combination is not available yet. Please choose different cases or buy a single case.';
        error.hidden = false;
        return;
      }
      body.set('id', nativeBundle.id);
    } else if (extras.length) {
      const quantity = body.get('quantity') || '1';
      const ids = [body.get('id'), ...extras.map((select) => select.value)];
      body.delete('id');
      body.delete('quantity');
      ids.forEach((id, index) => {
        body.append(`items[${index}][id]`, id);
        body.append(`items[${index}][quantity]`, quantity);
      });
    }
    const message = picker && picker.querySelector('[data-bundle-error]');
    if (message) message.hidden = true;
    form.dataset.submitting = 'true';
    buttons.forEach(button => { button.disabled = true; });
    setLabels(config.strings.adding);
    try {
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Unable to add these cases. Please try again.');
      }
      const added = await response.json();
      track('add_to_cart', {
        product_id: added.product_id, variant_id: added.variant_id, title: added.product_title,
        price: Number(added.final_price || added.price || 0) / 100, quantity: added.quantity || 1,
      });
      await refreshCart();
      setLabels(config.strings.added);
      openDrawer();
    } catch (error) {
      setLabels(config.strings.addError);
      if (message) { message.textContent = error.message; message.hidden = false; }
    } finally {
      setTimeout(() => {
        form.dataset.submitting = 'false';
        const variantSelect = section && section.querySelector('[data-variant-select]');
        states.forEach(({ button, label, disabled }) => {
          const unavailable = variantSelect ? variantSelect.selectedOptions[0].dataset.available !== 'true' : disabled;
          label.textContent = unavailable ? button.dataset.labelSoldOut : button.dataset.labelAdd;
          button.disabled = Boolean(unavailable);
        });
      }, 1600);
    }
  }, { capture: true });

  document.addEventListener('click', async (event) => {
    const opener = event.target.closest('[data-cart-open]');
    if (opener && drawer() && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      closeMenu();
      openDrawer();
      return;
    }
    if (event.target.closest('[data-drawer-close]')) {
      closeDrawer();
      return;
    }
    const change = event.target.closest('[data-line-change]');
    if (change) {
      event.preventDefault();
      const el = change.closest('[data-cart-section]') || drawer();
      if (el.classList.contains('is-busy')) return;
      el.classList.add('is-busy');
      const row = change.closest('.cart-line');
      const previousQuantity = Number(row?.dataset.analyticsQuantity);
      try {
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: change.dataset.key, quantity: Number(change.dataset.quantity) }),
        });
        if (!response.ok) throw new Error('change failed');
        const updatedCart = await response.json();
        let updatedItem = updatedCart.items.find(item => item.key === change.dataset.key);
        // Shopify may change a line key when automatic discounts change.
        if (!updatedItem && Number(change.dataset.quantity) > 0) {
          const matches = updatedCart.items.filter(item => String(item.variant_id) === row?.dataset.analyticsVariant);
          if (matches.length === 1) updatedItem = matches[0];
        }
        const quantity = updatedItem?.quantity ?? (Number(change.dataset.quantity) === 0 ? 0 : previousQuantity);
        if (quantity !== previousQuantity) track(quantity === 0 ? 'cart_item_removed' : 'cart_quantity_changed', {
          product_id: row?.dataset.analyticsProduct, variant_id: row?.dataset.analyticsVariant,
          previous_quantity: previousQuantity, quantity, value: updatedCart.total_price / 100,
        });
        await refreshCart();
      } catch (error) {
        const message = el.querySelector('[data-cart-error]');
        if (message) { message.textContent = config.strings.cartError; message.hidden = false; }
      } finally {
        el.classList.remove('is-busy');
      }
    }
  });

  /* Checkout: show progress while Shopify opens the checkout. The button is not disabled,
     so its name="checkout" is still sent with the form. */
  document.addEventListener('submit', (event) => {
    const button = event.submitter && event.submitter.closest('[data-checkout-button]');
    if (!button) return;
    if (button.classList.contains('is-loading')) { event.preventDefault(); return; }
    track('checkout_clicked', { source: button.closest('#cart-drawer') ? 'drawer' : 'cart_page' });
    button.classList.add('is-loading');
    const label = button.querySelector('[data-checkout-label]');
    if (label) { button.dataset.labelDefault = label.textContent; label.textContent = button.dataset.labelLoading; }
  });
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-checkout-button].is-loading').forEach((button) => {
      button.classList.remove('is-loading');
      const label = button.querySelector('[data-checkout-label]');
      if (label && button.dataset.labelDefault) label.textContent = button.dataset.labelDefault;
    });
  });

  /* Reviews: post to the review bridge so the review lands in Shopify admin.
     Without a bridge URL, or when the bridge is down or misconfigured, the normal form runs and emails
     the store — with the bridge's error in the email, so a broken bridge gets noticed. */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-review-form]');
    if (!form || form.dataset.sent === 'true') return;
    const bridge = form.dataset.bridge;
    if (!bridge) return;
    event.preventDefault();
    const button = form.querySelector('[data-sending-button]');
    const label = button && button.querySelector('[data-sending-label]');
    const error = form.querySelector('[data-review-error]');
    const original = label ? label.textContent : '';
    const reset = () => {
      if (label) label.textContent = original;
      if (button) button.classList.remove('is-loading');
    };
    if (label) label.textContent = button.dataset.labelSending;
    if (button) button.classList.add('is-loading');
    if (error) error.hidden = true;
    const value = (name) => (form.querySelector(`[name="${name}"]`) || {}).value || '';
    let response = null;
    try {
      response = await fetch(bridge, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: form.dataset.productId,
          name: value('contact[name]'),
          email: value('contact[email]'),
          rating: value('contact[rating]'),
          text: value('contact[body]'),
          website: value('website'),
        }),
      });
    } catch (networkError) {
      // bridge unreachable: handled by the email fallback below
    }
    if (response && response.ok) {
      const details = form.querySelector('.review-form__toggle');
      if (details) details.hidden = true;
      const success = form.querySelector('[data-review-success]');
      if (success) success.hidden = false;
      return;
    }
    // the visitor can fix these; emailing them would only pass the problem to the store owner
    if (response && (response.status === 400 || response.status === 429)) {
      reset();
      if (error) {
        error.textContent = response.status === 429 ? config.strings.reviewBusy : config.strings.reviewInvalid;
        error.hidden = false;
      }
      return;
    }
    const code = response ? await response.json().then((body) => body.error).catch(() => '') : '';
    const reason = response ? `${response.status} ${code}`.trim() : 'unreachable';
    console.warn('review bridge failed:', reason);
    const note = document.createElement('input');
    note.type = 'hidden';
    note.name = 'contact[review_bridge_error]';
    note.value = reason;
    form.appendChild(note);
    form.dataset.sent = 'true';
    reset();
    form.submit();
  });

  /* Contact forms: show "Sending…" (the button stays enabled so the browser still submits it). */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-sending-form]');
    const button = form && form.querySelector('[data-sending-button]');
    if (!button) return;
    if (button.classList.contains('is-loading')) { event.preventDefault(); return; }
    button.classList.add('is-loading');
    const label = button.querySelector('[data-sending-label]');
    if (label) label.textContent = button.dataset.labelSending;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeDrawer();
    closeMenu();
  });

  /* Back button from checkout restores an old snapshot of the page. */
  window.addEventListener('pageshow', (event) => { if (event.persisted) refreshCart().catch(() => {}); });

  /* One cart UI: with the drawer on, /cart opens the drawer over the previous page
     instead of showing a second cart design. Without JavaScript the page still works. */
  const OPEN_CART_KEY = 'fluffy:open-cart';
  function redirectCartPageToDrawer() {
    const onCartPage = document.querySelector('[data-cart-section]');
    if (!onCartPage || !drawer()) return false;
    let back = root;
    try {
      const from = document.referrer ? new URL(document.referrer) : null;
      if (from && from.origin === window.location.origin && !from.pathname.startsWith(config.routes.cart)) back = from.href;
    } catch (error) { /* keep the homepage */ }
    sessionStorage.setItem(OPEN_CART_KEY, '1');
    window.location.replace(back);
    return true;
  }
  function openCartAfterRedirect() {
    try {
      if (sessionStorage.getItem(OPEN_CART_KEY)) {
        sessionStorage.removeItem(OPEN_CART_KEY);
        openDrawer();
      }
    } catch (error) { /* storage blocked */ }
  }

  /* ---------- Favorites (saved in this browser) ---------- */
  const FAVORITES_KEY = 'fluffy:favorites';

  function readFavorites() {
    try {
      const saved = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
      return Array.isArray(saved) ? saved : [];
    } catch (error) {
      return [];
    }
  }

  function renderFavorites() {
    const favorites = readFavorites();
    document.querySelectorAll('[data-favorite]').forEach((button) => {
      const active = favorites.includes(button.dataset.favorite);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? button.dataset.labelRemove : button.dataset.labelAdd);
    });
    document.querySelectorAll('[data-favorites-count]').forEach((el) => {
      el.textContent = favorites.length;
      el.hidden = favorites.length === 0;
    });
    document.querySelectorAll('[data-favorites-count-text]').forEach((el) => { el.textContent = favorites.length; });
    document.querySelectorAll('[data-favorites-page]').forEach((page) => renderFavoritesPage(page, favorites));
  }

  function renderFavoritesPage(page, favorites) {
    let shown = 0;
    page.querySelectorAll('[data-favorite-card]').forEach((card) => {
      const index = favorites.indexOf(card.dataset.favoriteCard);
      card.hidden = index === -1;
      card.style.order = index === -1 ? '' : String(index);
      if (index !== -1) shown += 1;
    });
    page.querySelector('[data-favorites-grid]').hidden = shown === 0;
    page.querySelector('[data-favorites-empty]').hidden = shown > 0;
    const summary = page.querySelector('[data-favorites-summary]');
    if (summary) {
      const template = shown === 0 ? summary.dataset.empty : (shown === 1 ? summary.dataset.one : summary.dataset.other);
      summary.textContent = template.replace('[count]', shown);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-favorite]');
    if (!button) return;
    event.preventDefault();
    const handle = button.dataset.favorite;
    const favorites = readFavorites();
    const next = favorites.includes(handle) ? favorites.filter((h) => h !== handle) : [...favorites, handle];
    try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(next)); } catch (error) { /* storage blocked */ }
    renderFavorites();
  });
  window.addEventListener('storage', (event) => { if (event.key === FAVORITES_KEY) renderFavorites(); });

  /* ---------- Product page: quantity, total, variants, gallery ---------- */
  function formatMoney(cents) {
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(config.locale || 'en', {
        style: 'currency', currency: config.currency || 'USD',
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }

  function updateProductTotal(section) {
    const button = section.querySelector('[data-add-to-cart]');
    const totals = section.querySelectorAll('[data-add-to-cart-total]');
    const input = section.querySelector('[data-quantity-input]');
    if (!button) return;
    const quantity = Math.max(1, parseInt(input ? input.value : '1', 10) || 1);
    const picker = section.querySelector('[data-bundle-picker]');
    const choices = picker ? [...picker.querySelectorAll('[data-bundle-choice]:checked')] : [];
    const pack = picker && picker.querySelector('[data-bundle-pack][aria-pressed="true"]');
    const unitTotal = pack?.dataset.packVariant ? Number(pack.dataset.packPrice) : Number(button.dataset.unitPrice) + choices.reduce((sum, choice) => sum + Number(choice.dataset.price || 0), 0);
    const complete = choices.length === Number(picker?.dataset.packSize || 1) - 1;
    totals.forEach(total => { total.textContent = complete ? `· ${formatMoney(unitTotal * quantity)}` : ''; });
    if (picker) {
      const count = (1 + choices.length) * quantity;
      picker.querySelector('[data-bundle-summary]').textContent = complete ? `${count} ${count === 1 ? 'case' : 'cases'} · ${formatMoney(unitTotal * quantity)}` : 'Choose your extra cases to see the pack total.';
    }
  }

  function updateBundle(section) {
    const picker = section.querySelector('[data-bundle-picker]');
    if (!picker) return;
    picker.hidden = false;
    const limit = Number(picker.dataset.packSize || 1) - 1;
    picker.querySelector('[data-bundle-panel]').hidden = limit === 0;
    picker.querySelector('[data-bundle-reset]').hidden = limit === 0;
    picker.querySelector('[data-bundle-submit]').hidden = limit === 0;
    picker.querySelectorAll('[data-bundle-pack]').forEach((button) => {
      const active = Number(button.dataset.bundlePack) === limit + 1;
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-expanded', String(active));
    });
    const choices = [...picker.querySelectorAll('[data-bundle-choice]')];
    choices.filter((choice) => choice.checked).slice(limit).forEach((choice) => { choice.checked = false; });
    const selected = choices.filter((choice) => choice.checked);
    picker.querySelectorAll('[data-bundle-preview]').forEach((slot) => {
      const choice = selected[Number(slot.dataset.bundlePreview)];
      const source = choice?.closest('.bundle-picker__card').querySelector('img');
      const image = slot.querySelector('[data-bundle-preview-image]');
      image.hidden = !source;
      slot.querySelector('.bundle-picker__mystery').hidden = Boolean(source);
      if (source) { image.src = source.currentSrc || source.src; image.alt = source.alt; }
      else { image.removeAttribute('src'); image.alt = ''; }
    });
    choices.forEach((choice) => {
      choice.disabled = !choice.checked && (selected.length >= limit || selected.some((other) => other.dataset.product === choice.dataset.product));
    });
    picker.querySelector('[data-bundle-instruction]').textContent = `Choose ${limit} extra ${limit === 1 ? 'case' : 'cases'}`;
    picker.querySelector('[data-bundle-selection]').textContent = `${selected.length} of ${limit} selected${selected.length === limit ? ' — uncheck a case to swap it.' : ''}`;
    picker.querySelector('[data-bundle-error]').hidden = true;
    updateProductTotal(section);
  }
  const bundleSelections = new WeakMap();
  function bundleSelection(section) {
    const picker = section.querySelector('[data-bundle-picker]');
    const variant = section.querySelector('[data-add-to-cart-form] [name="id"]')?.value;
    const choices = [...picker.querySelectorAll('[data-bundle-choice]:checked')];
    return {
      pack_size: Number(picker.dataset.packSize),
      variant_ids: [variant, ...choices.map(choice => choice.value)].filter(Boolean),
      complete: choices.length === Number(picker.dataset.packSize) - 1,
    };
  }
  function bundleSelectionKey(selection) {
    return JSON.stringify([selection.pack_size, [...selection.variant_ids].sort()]);
  }
  function trackBundle(section) {
    const selection = bundleSelection(section);
    const key = bundleSelectionKey(selection);
    if (bundleSelections.get(section) === key) return;
    bundleSelections.set(section, key);
    track('bundle_selected', selection);
  }
  function initializeBundle(section) {
    updateBundle(section);
    if (section.querySelector('[data-bundle-picker]')) {
      bundleSelections.set(section, bundleSelectionKey(bundleSelection(section)));
    }
  }
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-bundle-choice]')) {
      const section = event.target.closest('[data-product-section]');
      updateBundle(section);
      trackBundle(section);
    }
  });
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-bundle-pack], [data-bundle-reset]');
    if (!button) return;
    const picker = button.closest('[data-bundle-picker]');
    const requestedSize = button.dataset.bundlePack;
    picker.dataset.packSize = requestedSize || '1';
    updateBundle(button.closest('[data-product-section]'));
    trackBundle(button.closest('[data-product-section]'));
  });
  document.querySelectorAll('[data-product-section]').forEach(initializeBundle);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-product-section]').forEach(initializeBundle));

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-quantity-step]');
    if (step) {
      const input = step.closest('[data-quantity]').querySelector('[data-quantity-input]');
      const next = (parseInt(input.value, 10) || 1) + Number(step.dataset.quantityStep);
      input.value = Math.min(Number(input.max) || 99, Math.max(Number(input.min) || 1, next));
      updateProductTotal(step.closest('[data-product-section]'));
      return;
    }
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) {
      const section = thumb.closest('[data-product-section]');
      const main = section.querySelector('[data-gallery-main]');
      if (main) {
        main.src = thumb.dataset.src;
        main.srcset = thumb.dataset.srcset;
        main.alt = thumb.dataset.alt;
      }
      section.querySelectorAll('[data-gallery-thumb]').forEach((el) => el.classList.toggle('is-active', el === thumb));
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-quantity-input]')) updateProductTotal(event.target.closest('[data-product-section]'));
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;
    const section = select.closest('[data-product-section]');
    const option = select.selectedOptions[0];
    const available = option.dataset.available === 'true';
    section.querySelector('[data-variant-id]').value = option.value;
    const price = section.querySelector('[data-product-price] .price');
    if (price) price.outerHTML = option.dataset.priceHtml;
    const submitting = section.querySelector('[data-add-to-cart-form]')?.dataset.submitting === 'true';
    section.querySelectorAll('[data-add-to-cart]').forEach(button => {
      button.dataset.unitPrice = option.dataset.price;
      button.disabled = submitting || !available;
      const label = button.querySelector('[data-add-to-cart-label]');
      if (!submitting) label.textContent = available ? button.dataset.labelAdd : button.dataset.labelSoldOut;
      const total = button.querySelector('[data-add-to-cart-total]');
      if (total) total.hidden = !available;
    });
    updateProductTotal(section);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
  });

  /* ---------- Mobile menu ---------- */
  function toggleMenu(open) {
    const menu = document.querySelector('[data-mobile-menu]');
    const toggle = document.querySelector('[data-menu-toggle]');
    if (!menu || !toggle) return;
    const isOpen = !menu.hidden;
    if (open === isOpen) return;
    menu.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    toggle.setAttribute('aria-label', open ? toggle.dataset.labelClose : toggle.dataset.labelOpen);
    if (open) lockScroll(); else unlockScroll();
  }
  function closeMenu() { toggleMenu(false); }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-menu-toggle]')) {
      const menu = document.querySelector('[data-mobile-menu]');
      toggleMenu(menu ? menu.hidden : false);
    } else if (event.target.closest('[data-menu-close]')) {
      closeMenu();
    }
  });

  /* ---------- Init (and again when sections reload in the theme editor) ---------- */
  renderFavorites();
  document.addEventListener('shopify:section:load', renderFavorites);
  if (!redirectCartPageToDrawer()) openCartAfterRedirect();
})();
